import { useEasykitPlanningStore } from '@/stores/easykitPlanning';
import { getTaskType } from '@/utils/easykit';
import { EasykitModeBanner } from './EasykitModeBanner';
import { useEasykitTaskDisplay } from './useEasykitTaskDisplay';
import type { OdooTask } from '@/types/odoo';

const TYPE_BAR_COLORS: Record<string, string> = {
  druktest: 'bg-red-500',
  levering: 'bg-blue-500',
  feestdag: 'bg-purple-500',
  other:    'bg-gray-400',
};

/** Top-level banner that picks the right transient mode (schedule / create / move) and renders EasykitModeBanner. */
export function EasykitMoveBanner() {
  const moveMode = useEasykitPlanningStore((s) => s.moveMode);
  const exitMoveMode = useEasykitPlanningStore((s) => s.exitMoveMode);
  const scheduleMode = useEasykitPlanningStore((s) => s.scheduleMode);
  const exitScheduleMode = useEasykitPlanningStore((s) => s.exitScheduleMode);
  const createMode = useEasykitPlanningStore((s) => s.createMode);
  const exitCreateMode = useEasykitPlanningStore((s) => s.exitCreateMode);

  if (scheduleMode) {
    const taskType = getTaskType(scheduleMode.taskName, null);
    const sideBar = TYPE_BAR_COLORS[taskType] || TYPE_BAR_COLORS.other;
    const title = scheduleMode.projectName
      ? `Inplannen: ${scheduleMode.taskName} — ${scheduleMode.projectName}`
      : `Inplannen: ${scheduleMode.taskName}`;
    const subline = [scheduleMode.address, scheduleMode.orderNumber].filter(Boolean).join(' · ') || null;
    return (
      <EasykitModeBanner tone="blue" sideBarColor={sideBar} title={title} subline={subline} hint="Klik op een cel om in te plannen" onCancel={exitScheduleMode} />
    );
  }

  if (createMode) {
    const title = createMode.type === 'feestdag' ? 'Feestdag inplannen'
                : createMode.type === 'verlof'   ? 'Verlof inplannen'
                : `Vrije taak inplannen: ${createMode.name ?? ''}`;
    return (
      <EasykitModeBanner tone="purple" sideBarColor="bg-purple-500" title={title} hint="Klik op een cel om te plaatsen" onCancel={exitCreateMode} />
    );
  }

  if (!moveMode) return null;
  return <MoveModeBanner task={moveMode.task} onCancel={exitMoveMode} />;
}

/** Sub-component so the move banner can call useEasykitTaskDisplay (a hook). */
function MoveModeBanner({ task, onCancel }: { task: OdooTask; onCancel: () => void }) {
  const { taskType, projectName, address } = useEasykitTaskDisplay(task);
  const title = projectName ? `Verplaatsen: ${task.name} — ${projectName}` : `Verplaatsen: ${task.name}`;
  return (
    <EasykitModeBanner tone="amber" sideBarColor={TYPE_BAR_COLORS[taskType]} title={title} subline={address || null} onCancel={onCancel} />
  );
}
